import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, memo, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { activity, installActivity } from '../app/activity'
import { ChunkBoundary, RouteUnavailable } from '../app/components/ChunkBoundary'
import { useReducedMotion } from '../app/hooks'
import { navigate, replaceSearch, takeReturnKey, usePath } from '../app/router'
import { SheetHost, preloadSheets } from '../app/sheets/SheetHost'
import { timeCursor } from '../app/time/cursor'
import { useUi } from '../app/ui'
import { selectedPlace, useApp, stepPlace } from './store'
import { useV5Data } from './data/useV5Data'
import type { V5Data } from './data/types'
import { Hero } from './zones/hero/Hero'
import { Story } from './zones/hero/Story'
import { Places } from './zones/places/Places'
import { Hourly } from './v7/Hourly'
import { PlacePalette } from './zones/places/PlacePalette'
import { Panorama } from './zones/panorama/Panorama'
import { TenDays } from './zones/days/TenDays'
import { Alerts } from './zones/alerts/Alerts'
import { LiveAlertsProvider } from './shell/LiveAlerts'
import { Settings } from './zones/settings/Settings'
import { PhoneNavigation } from './zones/navigation/PhoneNavigation'
import { Embeds } from './zones/embeds/Embeds'
import { openPlaces, openSettings, openForecast, goNow } from './shell/actions'
import { SkyLayer } from './shell/SkyLayer'
import { NeighbourPlaces } from './shell/NeighbourPlaces'
import { applyTimeLink, sectionFor, v5Route } from './shell/routing'
import { frameAtCursor } from './shell/bindings'
import { afterPaint } from './shell/afterPaint'
import { usePlaceTransition } from './shell/placeTransition'
import { METRIC_UNITS, US_UNITS } from '../shared/units'
import './styles/index.css'
import './styles/motion.css'
import './v7/v7.css'

const Studio = lazy(() => import('../studio/StudioPage'))
// Starts downloading at once, in parallel with the first paint; placeholders hold its cells.
const lowerHome = import('./LowerHome')
const LowerHome = lazy(() => lowerHome)
export const createQueryClient = () => new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } })

export function V5App({ client }: { client?: QueryClient }) {
  const [cache] = useState(() => client ?? createQueryClient())
  return <QueryClientProvider client={cache}><Root /></QueryClientProvider>
}

function Root() {
  const place = useApp(selectedPlace)
  const data = useV5Data(place)
  const path = usePath()
  const route = v5Route(path)
  const map = route === '/map' || route === '/radar'
  const studio = route === '/studio'
  const reduced = useReducedMotion()
  const sheet = useUi((state) => state.sheet)
  const list = useUi((state) => state.phoneList)
  useEffect(() => installActivity(), [])
  useEffect(() => {
    let stop = () => {}
    const cancel = afterPaint(() => { stop = preloadSheets() })
    return () => { cancel(); stop() }
  }, [])
  useEffect(() => { activity.setSheetOpen(!!sheet || list || route === '/settings') }, [sheet, list, route])
  useLayoutEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-v5', ''); root.setAttribute('data-doc-scroll', '')
    root.setAttribute('data-booted', '')
    return () => { root.removeAttribute('data-v5'); root.removeAttribute('data-doc-scroll') }
  }, [])
  useEffect(() => { document.documentElement.toggleAttribute('data-reduced-motion', reduced) }, [reduced])
  useEffect(() => {
    const update = () => {
      const frame = frameAtCursor(data)
      document.title = `${map ? 'Radar · ' : studio ? 'Widget Studio · ' : ''}${data.name}${frame ? ` · ${frame.temperature} ${frame.description}` : ''} — Weather Fusion`
    }
    update(); return timeCursor.subscribe(update)
  }, [data, map, studio])
  useLayoutEffect(() => { applyTimeLink(data.models) }, [data.models, path])
  useEffect(() => {
    if (route === '/places') openPlaces()
    const target = sectionFor[route]
    if (target) document.getElementById(target)?.scrollIntoView({ block: 'start', behavior: reduced ? 'instant' : 'smooth' })
    const key = takeReturnKey()
    if (key && !map && !studio) document.querySelector<HTMLElement>(`[data-nav-key="${key}"]`)?.focus({ preventScroll: true })
  }, [route, map, studio, reduced])
  useEffect(() => {
    if (route === 'notfound' || studio) return
    let queued: URLSearchParams | null = null, paint = 0, timer = 0
    const flush = () => { if (queued) replaceSearch(queued); queued = null; paint = timer = 0 }
    const sync = () => {
      const params = new URLSearchParams(location.search)
      if (place.isCurrent) { params.delete('lat'); params.delete('lon'); params.delete('name') }
      else { params.set('lat', String(place.latitude)); params.set('lon', String(place.longitude)) }
      if (timeCursor.selected == null) params.delete('t')
      else params.set('t', String(timeCursor.selected))
      const matches = (preset: typeof US_UNITS) => (Object.keys(preset) as (keyof typeof US_UNITS)[]).every((key) => preset[key] === data.units[key])
      if (matches(US_UNITS)) params.set('units', 'us')
      else if (matches(METRIC_UNITS)) params.set('units', 'metric')
      else params.delete('units') // Custom units persist in the store without a contradictory URL preset.
      queued = params
      // History snapshots scroll/layout. Serialize after paint, outside the cursor/place commit.
      if (!paint && !timer) paint = requestAnimationFrame(() => { timer = window.setTimeout(flush, 0) })
    }
    const unsubscribe = timeCursor.subscribe(sync)
    window.addEventListener('pagehide', flush)
    sync()
    return () => { unsubscribe(); cancelAnimationFrame(paint); clearTimeout(timer); window.removeEventListener('pagehide', flush) }
  }, [place.latitude, place.longitude, place.isCurrent, route, studio, data.units])
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const field = (event.target as HTMLElement)?.closest('input,textarea,select,[contenteditable="true"],dialog')
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); openPlaces(); return }
      if (field || sheet || list || route === '/settings' || studio) return
      if (event.key === '/') { event.preventDefault(); openPlaces() }
      else if (event.key === '[' || event.key === ']') { event.preventDefault(); stepPlace(event.key === '[' ? -1 : 1) }
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); timeCursor.stepHours((event.key === 'ArrowLeft' ? -1 : 1) * (event.altKey ? 24 : event.shiftKey ? 6 : 1)); timeCursor.flush() }
      else if (event.key.toLowerCase() === 'n' || event.key === 'Home') { event.preventDefault(); goNow() }
      else if (event.key === 'Escape') { if (map) navigate('/'); else goNow() }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [sheet, list, route, map, studio])
  return <div className="v5 v5-app" data-map-open={map || undefined} data-studio-open={studio || undefined}>
    <SkyLayer data={data} reduced={reduced} obscured={!!sheet || list || studio || map || route === '/settings'} />
    <a className="v5-skip" href="#v5-main">Skip to forecast</a>
    <div className="v5-home" aria-hidden={studio || route === 'notfound' || undefined}>
      <Masthead path={route} />
      <Places data={data} />
      <LiveAlertsProvider data={data}><Home data={data} map={map} blend={route === '/models'} /></LiveAlertsProvider>
      <Embeds />
    </div>
    {route === 'notfound' ? <div className="v5-notfound"><h1>Page not found</h1><p>The link doesn’t point to a Weather Fusion view.</p><button className="v5-text-link" onClick={openForecast}>Return to the forecast →</button></div> : null}
    {studio ? <div className="studio-scroller"><ChunkBoundary fallback={<RouteUnavailable what="Widget Studio" />}><Suspense fallback={<div className="v5-studio-pending"><h2>Widget Studio</h2><p>Opening your workspace…</p><button onClick={() => navigate('/')}>Back to the forecast</button></div>}><Studio onClose={() => navigate('/')} /></Suspense></ChunkBoundary></div> : null}
    <PhoneNavigation path={route} />
    <NeighbourPlaces data={data} />
    <PlacePalette data={data} />
    <Settings data={data} open={route === '/settings'} />
    <SheetHost retained />
  </div>
}

const Masthead = memo(function Masthead({ path }: { path: string }) {
  return <header className="v5-masthead"><button className="v5-wordmark" onClick={openForecast} aria-label="Weather Fusion home">weather<span>fusion</span></button><nav aria-label="Primary navigation"><button aria-current={path === '/' || path === '/forecast' ? 'page' : undefined} onClick={openForecast}>Forecast</button><button aria-current={path === '/days' ? 'page' : undefined} onClick={() => navigate('/days')}>10 days</button><button aria-current={path === '/map' ? 'page' : undefined} onClick={() => navigate('/map')}>Radar & map</button></nav><div className="v5-masthead-tools"><button onClick={openSettings} aria-label="Open settings and units">Settings</button><button onClick={openPlaces}>Places <kbd>⌘K</kbd></button></div></header>
})

const Home = memo(function Home({ data, map, blend }: { data: V5Data; map: boolean; blend: boolean }) {
  const ref = useRef<HTMLElement>(null)
  usePlaceTransition(ref, data.place.id)
  return <main ref={ref} id="v5-main" data-qa="v5-home" aria-label="Weather briefing">
    <div className="v5-layout v7-grid">
      <div className="v7-card v7-now"><Hero data={data} /><Story data={data} /><Alerts data={data} /></div>
      <Hourly data={data} />
      <Panorama data={data} />
      <TenDays data={data} />
      <ChunkBoundary fallback={<RouteUnavailable what="More of your forecast" />}><Suspense fallback={<LowerPlaceholders />}><LowerHome data={data} map={map} blend={blend} /></Suspense></ChunkBoundary>
    </div>
  </main>
})

/** Same grid cells the lazy cards fill, so their arrival never moves anything. */
function LowerPlaceholders() {
  return <>{['nowcast', 'record', 'conditions', 'radar'].map((area) => <div key={area} className={`v7-card v7-placeholder v7-area-${area}`} role="status" aria-label="Loading" />)}</>
}
